import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { UiComponent } from '@df/ui/ui.component';
import { NUserNotifications, UserNotificationsService } from '../user-notifications.service';
import { UserNotificationModalItemComponent } from './item/user-notification-modal-item.component';

export namespace NUserNotificationModal {
    export const MODAL_ID = 'user-notification-modal';

    export interface IModalData {
        notifications: NUserNotifications.INotification[];
    }
}

@DfModal({
    id: NUserNotificationModal.MODAL_ID,
    name: 'User Notification Modal',
    position: 'custom',
    config: {
        maxWidth: '100vw',
        width: '100vw',
        height: 'auto',
        maxHeight: '80vh',
        modalClass: 'p-a-0',
        panelClass: '_userNotificationModal',
        position: { bottom: '88px', left: '0px', right: '0px' },
        closeOnNavigation: true,
        hasBackdrop: false,
        scrollStrategy: 'noop',
        transformOptions: 'translate3d(100%, 0px, 0px)',
        transitionOptions: '500ms ease-in-out'
    },
    reopenPolicy: NDfModal.ReopenPolicy.passData,
    sizeRestriction: ['S', 'M', 'L', 'X']
})
@Component({
    selector: 'app-user-notification-modal',
    templateUrl: './user-notification-modal.component.html',
    styleUrls: ['./user-notification-modal.component.scss'],
    standalone: true,
    imports: [CommonModule, UserNotificationModalItemComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserNotificationModalComponent extends UiComponent implements OnInit, NDfModal.IModalComponent {
    private modalRef = inject(ModalRef<UserNotificationModalComponent>);
    protected data = inject(MODAL_DATA) as NUserNotificationModal.IModalData;
    private userNotificationsService = inject(UserNotificationsService);

    private closedIds = signal<string[]>([]);
    private allNotifications = signal<NUserNotifications.INotification[]>([]);

    // Computed signal for notifications (filtered and sorted)
    private notifications = computed(() => {
        const allNotifs = this.allNotifications();
        const closedIds = this.closedIds();

        return allNotifs.filter(i => !closedIds.includes(i.id)).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });

    // Computed signal for active notifications
    private activeNotifications = computed(() => this.notifications().filter(n => !this.closedIds().includes(n.id)));

    // Computed signal for visible notifications (oldest 3)
    protected visibleNotifications = computed(() => {
        const active = this.activeNotifications();
        return active.slice(0, 3);
    });

    // Effect to automatically close modal when no notifications remain
    private closeIfNoNotifications = effect(() => {
        if (this.activeNotifications().length === 0) {
            this.close();
        }
    });

    // Effect to mark visible notifications as seen
    // private markVisibleNotificationsAsSeen = effect(() => {
    //     const visible = this.visibleNotifications();
    //     // this.userNotificationsService.markNotificationsAsSeen(undefined, { ids: visible.map(n => n.id) });
    // });

    override ngOnInit(): void {
        this.updateModalData(this.data);
        super.ngOnInit();
    }

    updateModalData = (data: NUserNotificationModal.IModalData): void => {
        const currentAll = this.allNotifications();
        const newNotifications = data.notifications.filter(i => !currentAll.some(n => n.id === i.id));

        this.allNotifications.set([...currentAll, ...newNotifications]);
    };

    protected close(): void {
        this.modalRef.close();
    }

    protected handleNotificationClose(notificationId: string): void {
        // Add the closed notification ID to the closedIds signal
        const currentClosedIds = this.closedIds();
        this.closedIds.set([...currentClosedIds, notificationId]);
        this.userNotificationsService.markNotificationsAsSeen(undefined, { ids: [notificationId] });
    }
}
